/**
 * RefundOrderDialog — guided modal for admins to refund a PAID order (Plan 1929).
 *
 * Books an ERPNext-correct Credit Note + refund Payment Entry (the sale stays on
 * the books). Three modes:
 *   full   — refund the whole refundable amount
 *   custom — refund an arbitrary baht amount (rates scaled server-side)
 *   items  — refund selected items / quantities
 *
 * Step 1: choose mode · Step 2: details (amount/items + required reason + bank ref)
 * Success: confirmation with auto-close.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  CheckCircle2,
  Undo2,
  CircleDollarSign,
  PackageOpen,
  ArrowLeft,
  Landmark,
  Wallet,
  ArrowRightLeft,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  useRefundEligibility,
  useRefundOrder,
  type RefundItemInput,
} from "../api/salesOrderHooks";

const MODE_OPTIONS = [
  { value: "Bank Transfer", label: "โอนเงิน", Icon: Landmark },
  { value: "Cash", label: "เงินสด", Icon: Wallet },
  { value: "Wire Transfer", label: "โอนผ่านธนาคาร", Icon: ArrowRightLeft },
] as const;

interface RefundOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderName: string;
  currency: string;
}

type RefundMode = "full" | "custom" | "items" | null;
type Step = "choose" | "details" | "success";

function formatMoney(n: number, currency: string) {
  return (
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n) + ` ${currency}`
  );
}

export function RefundOrderDialog({
  open,
  onOpenChange,
  salesOrderName,
  currency,
}: RefundOrderDialogProps) {
  const [step, setStep] = useState<Step>("choose");
  const [mode, setMode] = useState<RefundMode>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [modeOfPayment, setModeOfPayment] = useState("Bank Transfer");
  const [referenceNo, setReferenceNo] = useState("");
  const [referenceDate, setReferenceDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  // item mode: sales_invoice_item -> qty to refund
  const [itemQtys, setItemQtys] = useState<Record<string, number>>({});

  const eligibilityQuery = useRefundEligibility(salesOrderName, open);
  const refundMutation = useRefundOrder(salesOrderName);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const eligibility = eligibilityQuery.data;
  const refundable = eligibility?.refundable ?? 0;
  const cur = eligibility?.currency || currency;
  const items = eligibility?.items ?? [];

  useEffect(() => {
    if (!open) {
      setStep("choose");
      setMode(null);
      setAmount("");
      setReason("");
      setModeOfPayment("Bank Transfer");
      setReferenceNo("");
      setReferenceDate(new Date().toISOString().slice(0, 10));
      setItemQtys({});
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    }
  }, [open]);

  const itemsTotal = useMemo(() => {
    return items.reduce((sum, it) => {
      const q = itemQtys[it.sales_invoice_item] || 0;
      return sum + q * it.rate;
    }, 0);
  }, [items, itemQtys]);

  const computedAmount =
    mode === "full" ? refundable : mode === "custom" ? parseFloat(amount) || 0 : itemsTotal;

  const reasonValid = reason.trim().length > 0;
  const amountValid =
    mode === "full"
      ? refundable > 0
      : mode === "custom"
        ? computedAmount > 0 && computedAmount <= refundable + 0.001
        : itemsTotal > 0;
  const canConfirm = reasonValid && amountValid && !refundMutation.isPending;

  const handleChoose = (m: RefundMode) => {
    setMode(m);
    if (m === "custom") setAmount("");
    if (m === "items") {
      const init: Record<string, number> = {};
      items.forEach((it) => (init[it.sales_invoice_item] = 0));
      setItemQtys(init);
    }
    setStep("details");
  };

  const handleBack = () => {
    setStep("choose");
    setMode(null);
  };

  const handleConfirm = () => {
    const refundItems: RefundItemInput[] | undefined =
      mode === "items"
        ? items
            .filter((it) => (itemQtys[it.sales_invoice_item] || 0) > 0)
            .map((it) => ({
              sales_invoice_item: it.sales_invoice_item,
              qty: itemQtys[it.sales_invoice_item],
            }))
        : undefined;

    refundMutation.mutate(
      {
        reason: reason.trim(),
        mode_of_payment: modeOfPayment,
        reference_no: referenceNo || undefined,
        reference_date: referenceDate || undefined,
        ...(mode === "custom" ? { amount: computedAmount } : {}),
        ...(mode === "items" ? { refund_items: refundItems } : {}),
      },
      {
        onSuccess: () => {
          setStep("success");
          autoCloseTimer.current = setTimeout(() => onOpenChange(false), 1800);
        },
        onError: (error) => {
          toast.error(error.message || "ไม่สามารถคืนเงินได้ กรุณาลองอีกครั้ง");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden">
        {/* ── Loading eligibility ── */}
        {step !== "success" && eligibilityQuery.isLoading && (
          <div className="flex flex-col items-center gap-3 py-14">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">กำลังตรวจสอบยอดคืนเงิน...</p>
          </div>
        )}

        {/* ── Error loading eligibility ── */}
        {step !== "success" && !eligibilityQuery.isLoading && eligibilityQuery.isError && (
          <div className="flex flex-col items-center gap-3 py-12 px-6 text-center">
            <div className="rounded-full bg-red-50 dark:bg-red-950/30 p-3">
              <AlertCircle className="h-7 w-7 text-red-500" />
            </div>
            <p className="text-base font-semibold">โหลดข้อมูลคืนเงินไม่สำเร็จ</p>
            <p className="text-sm text-muted-foreground">
              {(eligibilityQuery.error as Error)?.message || "กรุณาลองใหม่อีกครั้ง"}
            </p>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>
                ปิด
              </Button>
              <Button className="rounded-xl" onClick={() => eligibilityQuery.refetch()}>
                ลองใหม่
              </Button>
            </div>
          </div>
        )}

        {/* ── Not eligible ── */}
        {step !== "success" &&
          !eligibilityQuery.isLoading &&
          eligibility &&
          !eligibility.eligible && (
            <div className="flex flex-col items-center gap-3 py-12 px-6 text-center">
              <div className="rounded-full bg-amber-50 dark:bg-amber-950/30 p-3">
                <AlertCircle className="h-7 w-7 text-amber-500" />
              </div>
              <p className="text-base font-semibold">คืนเงินไม่ได้</p>
              <p className="text-sm text-muted-foreground">
                {eligibility.paid_status !== "Paid"
                  ? "ออเดอร์นี้ยังไม่ได้ชำระเงิน"
                  : "ออเดอร์นี้ถูกคืนเงินครบแล้ว"}
              </p>
              <Button variant="outline" className="mt-2 rounded-xl" onClick={() => onOpenChange(false)}>
                ปิด
              </Button>
            </div>
          )}

        {/* ── Success ── */}
        {step === "success" && (
          <div className="flex flex-col items-center gap-4 py-12 px-6">
            <div className="rounded-full bg-green-50 dark:bg-green-950/30 p-4">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-base font-semibold">คืนเงินเรียบร้อย</p>
              <p className="text-sm text-muted-foreground">{formatMoney(computedAmount, cur)}</p>
            </div>
          </div>
        )}

        {/* ── Step 1: choose mode ── */}
        {step === "choose" && eligibility?.eligible && (
          <div className="px-6 py-6 space-y-5">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">คืนเงิน</h2>
              <p className="text-sm text-muted-foreground">
                คืนได้สูงสุด {formatMoney(refundable, cur)}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <ModeCard
                onClick={() => handleChoose("full")}
                Icon={Undo2}
                title="คืนเต็มจำนวน"
                sub="Full refund"
                right={formatMoney(refundable, cur)}
                accent="rose"
              />
              <ModeCard
                onClick={() => handleChoose("custom")}
                Icon={CircleDollarSign}
                title="คืนบางส่วน (ระบุจำนวน)"
                sub="Custom amount"
                right="ระบุเอง"
                accent="amber"
              />
              <ModeCard
                onClick={() => handleChoose("items")}
                Icon={PackageOpen}
                title="เลือกสินค้าที่จะคืน"
                sub="By item"
                right={`${items.length} รายการ`}
                accent="sky"
              />
            </div>
          </div>
        )}

        {/* ── Step 2: details ── */}
        {step === "details" && eligibility?.eligible && (
          <div className="space-y-0">
            <div className="flex items-center gap-3 px-6 pt-5 pb-4">
              <button onClick={handleBack} className="rounded-full p-1.5 hover:bg-muted transition-colors">
                <ArrowLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              <h2 className="flex-1 text-base font-semibold">
                {mode === "full" ? "คืนเต็มจำนวน" : mode === "custom" ? "คืนบางส่วน" : "เลือกสินค้าที่จะคืน"}
              </h2>
              <span className="rounded-full bg-rose-50 dark:bg-rose-950/30 px-3 py-1 text-xs font-semibold text-rose-600">
                {formatMoney(computedAmount, cur)}
              </span>
            </div>

            <div className="space-y-4 px-6 pb-6 max-h-[60vh] overflow-y-auto">
              {/* Custom amount */}
              {mode === "custom" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">จำนวนเงินที่คืน</label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={refundable}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-lg font-semibold pr-14"
                      autoFocus
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {cur}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">คืนได้สูงสุด {formatMoney(refundable, cur)}</p>
                </div>
              )}

              {/* Items */}
              {mode === "items" && (
                <div className="space-y-2">
                  {items.map((it) => {
                    const q = itemQtys[it.sales_invoice_item] || 0;
                    const selected = q > 0;
                    return (
                      <div
                        key={it.sales_invoice_item}
                        className={cn(
                          "flex items-center gap-3 rounded-xl border p-3 transition-colors",
                          selected ? "border-rose-300 bg-rose-50/40 dark:bg-rose-950/10" : "border-border",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={it.refundable_qty <= 0}
                          onChange={(e) =>
                            setItemQtys((m) => ({
                              ...m,
                              [it.sales_invoice_item]: e.target.checked ? it.refundable_qty : 0,
                            }))
                          }
                          className="h-4 w-4 accent-rose-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.item_name || it.item_code}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatMoney(it.rate, cur)} · คืนได้ {it.refundable_qty}
                          </p>
                        </div>
                        <Input
                          type="number"
                          min="0"
                          max={it.refundable_qty}
                          step="1"
                          value={selected ? q : ""}
                          disabled={it.refundable_qty <= 0}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(it.refundable_qty, parseFloat(e.target.value) || 0));
                            setItemQtys((m) => ({ ...m, [it.sales_invoice_item]: v }));
                          }}
                          className="w-16 text-center"
                          placeholder="0"
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Reason (required) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  เหตุผลการคืนเงิน <span className="text-rose-500">*</span>
                </label>
                <Input
                  placeholder="เช่น สินค้าหมด / ลูกค้ายกเลิก"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  autoFocus={mode === "full"}
                />
              </div>

              {/* Mode of payment */}
              <div className="space-y-2">
                <label className="text-sm font-medium">วิธีคืนเงิน</label>
                <div className="grid grid-cols-3 gap-2">
                  {MODE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setModeOfPayment(opt.value)}
                      className={cn(
                        "flex items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-xs transition-all",
                        modeOfPayment === opt.value
                          ? "border-rose-400 bg-rose-50 dark:bg-rose-950/30 font-medium text-rose-700 dark:text-rose-300"
                          : "border-border hover:border-muted-foreground/40 hover:bg-muted/50",
                      )}
                    >
                      <opt.Icon className={cn("h-4 w-4", modeOfPayment === opt.value ? "text-rose-600" : "text-muted-foreground")} />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Reference no + date */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    เลขอ้างอิง <span className="text-muted-foreground font-normal">(ไม่บังคับ)</span>
                  </label>
                  <Input placeholder="เลขที่โอน" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">วันที่คืน</label>
                  <Input type="date" value={referenceDate} onChange={(e) => setReferenceDate(e.target.value)} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => onOpenChange(false)}
                  disabled={refundMutation.isPending}
                >
                  ยกเลิก
                </Button>
                <Button
                  className="flex-1 rounded-xl bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                >
                  {refundMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      กำลังคืนเงิน...
                    </>
                  ) : (
                    <>คืนเงิน {amountValid && `• ${formatMoney(computedAmount, cur)}`}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModeCard({
  onClick,
  Icon,
  title,
  sub,
  right,
  accent,
}: {
  onClick: () => void;
  Icon: typeof Undo2;
  title: string;
  sub: string;
  right: string;
  accent: "rose" | "amber" | "sky";
}) {
  const accents = {
    rose: "hover:border-rose-400 hover:bg-rose-50/50 dark:hover:bg-rose-950/20",
    amber: "hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/20",
    sky: "hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-950/20",
  } as const;
  const iconBg = {
    rose: "bg-rose-50 dark:bg-rose-950/30 text-rose-600",
    amber: "bg-amber-50 dark:bg-amber-950/30 text-amber-600",
    sky: "bg-sky-50 dark:bg-sky-950/30 text-sky-600",
  } as const;
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-2xl border-2 border-border bg-card p-4 text-left transition-all duration-150",
        "focus:outline-none focus:ring-2 focus:ring-rose-400/40",
        accents[accent],
      )}
    >
      <div className={cn("rounded-full p-2.5", iconBg[accent])}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      <span className="text-sm font-medium text-muted-foreground">{right}</span>
    </button>
  );
}
